import { useEffect, useMemo, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, MapPin, Clock, Coffee, LogOut, LogIn, Calendar, Trash2, Pencil, Pause, Car } from "lucide-react";
import api from "@/lib/api";
import SafetyLogTab from "./SafetyLogTab";
import SafetySignoff from "./SafetySignoff";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/hooks/useConfirm";
import { useLiveTick } from "@/hooks/useLiveTick";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import AddPunchDialog from "@/components/dashboard/AddPunchDialog";

/**
 * OperationsTab — owner / admin live view of who is on the clock, where,
 * and their punch trail for a given day. Prev/next day arrows + a date
 * picker so you can review historical days.
 */

const isoDay = (d) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${dy}`;
};

const todayIso = () => {
  // Business date in America/Toronto — punches are filed under this day, so
  // the Operations view must default to it regardless of device timezone.
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" });
};

const addDays = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return isoDay(dt);
};

const fmtDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

const fmtTime = (isoTs) => {
  if (!isoTs) return "—";
  try {
    return new Date(isoTs).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  } catch { return "—"; }
};

const fmtSince = (isoTs) => {
  if (!isoTs) return "";
  const secs = Math.max(0, Math.floor((Date.now() - new Date(isoTs).getTime()) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const fmtHours = (seconds) => {
  const s = Math.max(0, Number(seconds) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

const EVENT_META = {
  clock_in: { label: "Clock in", Icon: LogIn, tint: "text-emerald-800 dark:text-emerald-300", bg: "bg-emerald-500/15 border-emerald-500/40" },
  clock_out: { label: "Clock out", Icon: LogOut, tint: "text-red-800 dark:text-red-300", bg: "bg-red-500/15 border-red-500/40" },
  lunch_start: { label: "Lunch start", Icon: Coffee, tint: "text-amber-800 dark:text-amber-300", bg: "bg-amber-500/15 border-amber-500/40" },
  lunch_end: { label: "Lunch end", Icon: Coffee, tint: "text-sky-800 dark:text-sky-300", bg: "bg-sky-500/15 border-sky-500/40" },
  break_start: { label: "Break start", Icon: Pause, tint: "text-amber-800 dark:text-amber-300", bg: "bg-amber-500/15 border-amber-500/40" },
  break_end: { label: "Break end", Icon: Pause, tint: "text-sky-800 dark:text-sky-300", bg: "bg-sky-500/15 border-sky-500/40" },
  travel: { label: "Travel", Icon: Car, tint: "text-[#4f6fe8] dark:text-[#8fa4f0]", bg: "bg-[#1a3fd1]/10 border-[#1a3fd1]/40" },
  travel_end: { label: "Arrived", Icon: MapPin, tint: "text-emerald-800 dark:text-emerald-300", bg: "bg-emerald-500/15 border-emerald-500/40" },
};

const StatePill = ({ state }) => {
  const map = {
    in: { label: "On the clock", cls: "border-emerald-500/50 bg-emerald-500/15 text-emerald-800 dark:text-emerald-200" },
    lunch: { label: "On lunch", cls: "border-amber-500/50 bg-amber-500/15 text-amber-800 dark:text-amber-200" },
    idle: { label: "Clocked out", cls: "border-white/15 bg-white/5 text-white/70" },
  };
  const m = map[state] || map.idle;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${m.cls}`} data-testid={`ops-emp-state-${state}`}>
      <Clock className="h-3 w-3" />{m.label}
    </span>
  );
};

const EmployeeCard = ({ emp, canDelete, canEdit, onDeletePunch, onEditPunch }) => {
  const initials = (emp.name || "?").split(/\s+/).slice(0, 2).map((s) => s[0] || "").join("").toUpperCase();
  // Punch trail starts fully collapsed — "Show details" reveals every entry.
  const [showAll, setShowAll] = useState(false);
  const punches = emp.punches || [];
  const visiblePunches = showAll ? punches.map((p, i) => [p, i]) : [];
  return (
    <div
      data-testid={`ops-employee-card-${emp.employee_id}`}
      className="rounded-lg border border-white/10 bg-white/[0.02] p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full border border-white/15 bg-white/5">
            {emp.photo ? (
              <img src={emp.photo} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center font-mono text-sm text-white/60">{initials}</div>
            )}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">{emp.name}</p>
            <p className="truncate font-mono text-[10px] uppercase tracking-widest text-white/50">
              {emp.trade || emp.job_title || "—"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatePill state={emp.state} />
          <span className="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-white/70">
            {fmtHours(emp.paid_seconds)} paid
          </span>
        </div>
      </div>

      {emp.state === "in" && emp.open_since && (
        <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-emerald-800 dark:text-emerald-300">
          On the clock for {fmtSince(emp.open_since)} · since {fmtTime(emp.open_since)}
        </p>
      )}
      {emp.state === "lunch" && emp.on_lunch_since && (
        <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-amber-800 dark:text-amber-300">
          On lunch for {fmtSince(emp.on_lunch_since)} · since {fmtTime(emp.on_lunch_since)}
        </p>
      )}

      {punches.length > 0 ? (
        <>
        <ol className="mt-3 space-y-2">
          {visiblePunches.map(([p, idx]) => {
            const meta = EVENT_META[p.event] || { label: p.event, Icon: Clock, tint: "text-white/70", bg: "bg-white/5 border-white/15" };
            const { Icon } = meta;
            const hasGps = typeof p.lat === "number" && typeof p.lng === "number";
            const map = hasGps ? `https://www.google.com/maps?q=${p.lat},${p.lng}` : null;
            return (
              <li key={p.entry_id || idx} data-testid={`ops-punch-${emp.employee_id}-${idx}`} className={`flex flex-wrap items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs ${meta.bg}`}>
                <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest ${meta.tint}`}>
                  <Icon className="h-3 w-3" />{meta.label}
                </span>
                <span className="font-mono text-[11px] text-white/80">{fmtTime(p.timestamp)}</span>
                {p.next_day && (
                  <span
                    data-testid={`ops-punch-nextday-${emp.employee_id}-${idx}`}
                    title="This punch happened after midnight (the following day)"
                    className="rounded border border-violet-500/40 bg-violet-500/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-violet-700 dark:text-violet-300"
                  >
                    +1 day
                  </span>
                )}
                {p.job_name && (
                  <span className="rounded border border-white/10 bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-white/70">
                    {p.job_name}
                  </span>
                )}
                {p.note && (
                  <span
                    data-testid={`ops-punch-note-${emp.employee_id}-${idx}`}
                    className="basis-full pt-0.5 text-[11px] italic text-white/60"
                  >
                    &ldquo;{p.note}&rdquo;
                  </span>
                )}
                {hasGps ? (
                  <a
                    href={map}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`ops-punch-map-${emp.employee_id}-${idx}`}
                    className="ml-auto inline-flex items-center gap-1 rounded border border-[#1a3fd1]/40 bg-[#1a3fd1]/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-[#4f6fe8] hover:border-[#1a3fd1] hover:bg-[#1a3fd1]/20"
                    title={`${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}${p.accuracy ? ` · ±${Math.round(p.accuracy)}m` : ""}`}
                  >
                    <MapPin className="h-3 w-3" /> Map
                  </a>
                ) : (
                  <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-white/40">no GPS</span>
                )}
                {canEdit && p.entry_id && (
                  <button
                    type="button"
                    onClick={() => onEditPunch(p, emp)}
                    data-testid={`ops-punch-edit-${emp.employee_id}-${idx}`}
                    aria-label="Edit punch time"
                    title="Edit punch time (superadmin only)"
                    className="inline-flex h-6 w-6 items-center justify-center rounded border border-[#1a3fd1]/40 bg-[#1a3fd1]/10 text-[#8fa4f0] hover:border-[#1a3fd1] hover:bg-[#1a3fd1]/20"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
                {canDelete && p.entry_id && (
                  <button
                    type="button"
                    onClick={() => onDeletePunch(p, emp)}
                    data-testid={`ops-punch-delete-${emp.employee_id}-${idx}`}
                    aria-label="Delete punch"
                    title="Delete this punch"
                    className="inline-flex h-6 w-6 items-center justify-center rounded border border-red-500/40 bg-red-500/10 text-red-300 hover:border-red-500 hover:bg-red-500/20"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </li>
            );
          })}
        </ol>
        {punches.length > 0 && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            data-testid={`ops-punches-toggle-${emp.employee_id}`}
            className="mt-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#4f6fe8] hover:text-white"
          >
            {showAll ? "Hide details" : `Show details (${punches.length})`}
          </button>
        )}
        </>
      ) : (
        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/40">No punches recorded</p>
      )}
    </div>
  );
};

const OperationsTab = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const canDelete = user?.role === "owner" || user?.role === "superadmin";
  const canEdit = user?.role === "superadmin";
  const [date, setDate] = useState(todayIso());
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ date: todayIso(), employees: [] });
  const [tick, setTick] = useState(0);
  const [editing, setEditing] = useState(null); // { punch, emp, dateStr, timeStr, jobProjectId }
  const [projects, setProjects] = useState([]);
  const [addOpen, setAddOpen] = useState(false); // superadmin manual punch dialog
  const [allEmployees, setAllEmployees] = useState([]);
  useEffect(() => {
    if (!canEdit) return;
    api.get("/projects").then((r) => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [canEdit]);
  useEffect(() => {
    if (user?.role !== "superadmin") return;
    api.get("/employees").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.employees || []);
      setAllEmployees(list.filter((e) => (e.status || "Active") === "Active" && e.time_clock_enabled));
    }).catch(() => {});
  }, [user?.role]);

  const load = useCallback(async (d, silent = false) => {
    // silent = background poll: refresh the day view in place — no spinner,
    // no error toast on a transient blip (the next tick recovers).
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/operations/day", { params: { date: d } });
      setData(data);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Could not load operations");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);
  useLiveTick(useCallback(() => load(date, true), [load, date])); // impersonation live-watch

  const deletePunch = async (punch, emp) => {
    const evLabel = (EVENT_META[punch.event] || {}).label || punch.event;
    const ok = await confirm({
      title: "Delete this clock punch?",
      description: `${emp.name} · ${evLabel} at ${fmtTime(punch.timestamp)}${punch.job_name ? ` (${punch.job_name})` : ""}. This can't be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await api.delete(`/time-entries/${punch.entry_id}`);
      toast.success("Punch deleted");
      load(date);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete punch");
    }
  };

  // Superadmin-only: prefill dialog with the punch's local date + time
  // so a superadmin can nudge a missed clock-out to the correct minute.
  const openEditPunch = (punch, emp) => {
    const dt = new Date(punch.timestamp);
    const pad = (n) => String(n).padStart(2, "0");
    const dateStr = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
    const timeStr = `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    setEditing({
      punch,
      emp,
      dateStr,
      timeStr,
      jobProjectId: punch.job_project_id || "",
      jobName: punch.job_name || "",
    });
  };

  const saveEditPunch = async () => {
    if (!editing) return;
    const { punch, dateStr, timeStr, jobProjectId, jobName } = editing;
    if (!dateStr || !timeStr) {
      toast.error("Pick a date and time");
      return;
    }
    const [y, m, d] = dateStr.split("-").map(Number);
    const [hh, mm] = timeStr.split(":").map(Number);
    const local = new Date(y, m - 1, d, hh, mm, 0, 0);
    try {
      const body = { timestamp: local.toISOString() };
      if (punch.event === "clock_in") {
        body.job_project_id = jobProjectId;
        body.job_name = jobName;
      }
      await api.put(`/time-entries/${punch.entry_id}`, body);
      toast.success("Punch updated");
      setEditing(null);
      load(date);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update punch");
    }
  };

  // Live-refresh the "on the clock for" times every 30s and re-poll the
  // roster every 60s so the operations view stays fresh without hammering.
  useEffect(() => {
    const t1 = setInterval(() => setTick((v) => v + 1), 30_000);
    const t2 = setInterval(() => load(date, true), 60_000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [date, load]);

  const isToday = date === todayIso();
  const summary = useMemo(() => {
    const emps = data.employees || [];
    const on = emps.filter((e) => e.state === "in").length;
    const lunch = emps.filter((e) => e.state === "lunch").length;
    const totalPaid = emps.reduce((s, e) => s + (Number(e.paid_seconds) || 0), 0);
    return { on, lunch, tracked: emps.length, totalPaid };
  }, [data, tick]);

  return (
    <div className="space-y-4" data-testid="operations-tab">
      {/* Date navigator */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDate((d) => addDays(d, -1))}
            data-testid="ops-date-prev"
            aria-label="Previous day"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-white/15 bg-white/5 text-white/70 hover:border-white/25 hover:text-white"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="relative flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-3 py-2">
            <Calendar className="h-4 w-4 text-white/50" />
            <input
              type="date"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              data-testid="ops-date-input"
              className="bg-transparent font-mono text-xs uppercase tracking-widest text-white focus:outline-none"
            />
            <span className="hidden font-mono text-[10px] uppercase tracking-widest text-white/50 sm:inline">
              {fmtDate(date)}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setDate((d) => addDays(d, +1))}
            data-testid="ops-date-next"
            aria-label="Next day"
            disabled={isToday}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-white/15 bg-white/5 text-white/70 hover:border-white/25 hover:text-white disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          {!isToday && (
            <button
              type="button"
              onClick={() => setDate(todayIso())}
              data-testid="ops-date-today"
              className="rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white/70 hover:border-white/25 hover:text-white"
            >
              Today
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "superadmin" && (
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              data-testid="ops-add-punch-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-[#1a3fd1]/50 bg-[#1a3fd1]/15 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0] hover:bg-[#1a3fd1]/25"
            >
              + Add punch
            </button>
          )}
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3" data-testid="ops-summary-on">
          <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-800 dark:text-emerald-300">On clock</p>
          <p className="mt-1 text-2xl font-bold text-white">{summary.on}</p>
        </div>
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3" data-testid="ops-summary-lunch">
          <p className="font-mono text-[10px] uppercase tracking-widest text-amber-800 dark:text-amber-300">On lunch</p>
          <p className="mt-1 text-2xl font-bold text-white">{summary.lunch}</p>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3" data-testid="ops-summary-tracked">
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">Tracked today</p>
          <p className="mt-1 text-2xl font-bold text-white">{summary.tracked}</p>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3" data-testid="ops-summary-hours">
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">Paid so far</p>
          <p className="mt-1 text-2xl font-bold text-white">{fmtHours(summary.totalPaid)}</p>
        </div>
      </div>

      {/* Roster */}
      {loading && (data.employees || []).length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-8 text-center font-mono text-[11px] uppercase tracking-widest text-white/40" data-testid="ops-loading">
          Loading…
        </div>
      ) : (data.employees || []).length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-8 text-center" data-testid="ops-empty">
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">Nobody punched on {fmtDate(date)}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {data.employees.map((emp) => (
            <EmployeeCard key={emp.employee_id} emp={emp} canDelete={canDelete} canEdit={canEdit} onDeletePunch={deletePunch} onEditPunch={openEditPunch} />
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="ops-edit-punch-dialog">
          <DialogHeader>
            <DialogTitle>Edit punch time</DialogTitle>
            <DialogDescription>
              {editing && `${editing.emp.name} · ${(EVENT_META[editing.punch.event] || {}).label || editing.punch.event}`}
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Date</label>
                  <Input
                    type="date"
                    value={editing.dateStr}
                    onChange={(e) => setEditing((s) => ({ ...s, dateStr: e.target.value }))}
                    data-testid="ops-edit-punch-date"
                  />
                </div>
                <div>
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Time</label>
                  <Input
                    type="time"
                    value={editing.timeStr}
                    onChange={(e) => setEditing((s) => ({ ...s, timeStr: e.target.value }))}
                    data-testid="ops-edit-punch-time"
                  />
                </div>
              </div>
              {editing.punch.event === "clock_in" && (
                <>
                  <div>
                    <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Job / project</label>
                    <select
                      value={editing.jobProjectId || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, jobProjectId: e.target.value }))}
                      data-testid="ops-edit-punch-job"
                      className="h-10 w-full rounded-md border border-white/15 bg-[#0a0a0a] px-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
                    >
                      <option value="">— No job —</option>
                      {projects.map((p) => (
                        <option key={p.project_id} value={p.project_id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">
                      Or type a label
                    </label>
                    <Input
                      value={editing.jobName || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, jobName: e.target.value }))}
                      placeholder="e.g. Home, Working in truck, Doing payroll…"
                      data-testid="ops-edit-punch-jobname"
                    />
                    <p className="mt-1 font-mono text-[9.5px] text-white/40">
                      A free-text label overrides the dropdown — use this for things that aren&apos;t real projects.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEditPunch} data-testid="ops-edit-punch-save" className="bg-[#1a3fd1] text-white hover:bg-[#1633a3]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {addOpen && (
        <AddPunchDialog
          day={date}
          employees={allEmployees}
          onClose={() => setAddOpen(false)}
          onSaved={() => load(date)}
        />
      )}
    </div>
  );
};

/** Operations: live board, plus the Safety meeting log for owners/admins/superadmins. */
function OperationsWithSafety(props) {
  const { user } = useAuth();
  const [view, setView] = useState("board");
  const canLog = ["owner", "admin", "superadmin"].includes(user?.role);
  const canSafety = canLog || user?.role === "manager";   // foremen get the sign-off card only
  return (
    <div className="space-y-3">
      {canSafety && (
        <div className="flex w-fit gap-1 rounded-full border border-white/10 bg-white/5 p-0.5 text-[11px] font-semibold" data-testid="operations-subnav">
          <button onClick={() => setView("board")} className={`rounded-full px-3 py-1 ${view === "board" ? "bg-[#1a3fd1] text-white" : "text-white/60"}`} data-testid="operations-view-board">Live board</button>
          <button onClick={() => setView("safety")} className={`rounded-full px-3 py-1 ${view === "safety" ? "bg-[#1a3fd1] text-white" : "text-white/60"}`} data-testid="operations-view-safety">Safety meeting</button>
        </div>
      )}
      {view === "safety" && canSafety ? (
        <div className="space-y-4">
          <SafetySignoff canDeleteAny={["owner", "superadmin"].includes(user?.role)} />
          {canLog && <SafetyLogTab />}
        </div>
      ) : <OperationsTab {...props} />}
    </div>
  );
}

export default OperationsWithSafety;
